import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Search, Pencil, Trash2, Laptop, History } from "lucide-react";
import { toast } from "sonner";

const empty = {
  name: "", asset_type: "Computador", brand: "", model: "", serial_number: "",
  tag: "", assigned_to: "", department: "", location: "", status: "ativo",
  acquisition_date: "", notes: "",
};

const STATUS = {
  ativo: { label: "Ativo", cls: "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300" },
  manutencao: { label: "Manutenção", cls: "bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300" },
  inativo: { label: "Inativo", cls: "bg-zinc-200 text-zinc-800 dark:bg-zinc-700 dark:text-zinc-200" },
  estoque: { label: "Em estoque", cls: "bg-sky-100 text-sky-800 dark:bg-sky-500/20 dark:text-sky-300" },
};

const TYPES = ["Computador", "Notebook", "Monitor", "Impressora", "Celular", "Tablet", "Servidor", "Periférico", "Outro"];

export default function Assets() {
  const { user } = useAuth();
  const [assets, setAssets] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyAsset, setHistoryAsset] = useState(null);
  const [history, setHistory] = useState([]);

  const load = async () => {
    try {
      const r = await api.get("/assets");
      setAssets(r.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const loadUsers = async () => {
    try { const r = await api.get("/users"); setUsers(r.data); } catch { /* silent */ }
  };
  useEffect(() => { load(); loadUsers(); }, []);

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (a) => {
    setEditing(a);
    setForm({
      ...empty, ...a,
      assigned_to: a.assigned_to || "",
      brand: a.brand || "", model: a.model || "", serial_number: a.serial_number || "",
      tag: a.tag || "", department: a.department || "", location: a.location || "",
      acquisition_date: a.acquisition_date || "", notes: a.notes || "",
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, assigned_to: form.assigned_to || null };
      if (editing) await api.patch(`/assets/${editing.id}`, payload);
      else await api.post("/assets", payload);
      toast.success(editing ? "Ativo atualizado" : "Ativo cadastrado");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const remove = async (a) => {
    if (!confirm(`Excluir ${a.name}?`)) return;
    try { await api.delete(`/assets/${a.id}`); toast.success("Excluído"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const openHistory = async (a) => {
    setHistoryAsset(a); setHistoryOpen(true); setHistory([]);
    try { const r = await api.get(`/assets/${a.id}/history`); setHistory(r.data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const filtered = assets.filter((a) => {
    const matchSearch = !search
      || a.name.toLowerCase().includes(search.toLowerCase())
      || (a.serial_number || "").toLowerCase().includes(search.toLowerCase())
      || (a.tag || "").toLowerCase().includes(search.toLowerCase())
      || (a.assigned_to_name || "").toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === "all" || a.status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <div className="space-y-8" data-testid="assets-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Ativos</div>
          <h1 className="font-heading text-4xl font-semibold tracking-tight">Equipamentos & Funcionários</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Cadastre computadores, notebooks e demais ativos, e associe a cada funcionário responsável.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} data-testid="new-asset-btn"><Plus className="mr-2 h-4 w-4" /> Novo ativo</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editing ? "Editar ativo" : "Novo ativo"}</DialogTitle>
              <DialogDescription>Cadastre equipamentos e vincule-os a funcionários.</DialogDescription>
            </DialogHeader>
            <form onSubmit={save} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Nome / Identificação</Label>
                  <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Ex.: Dell Latitude 5420 - RH01" data-testid="asset-name-input" /></div>
                <div><Label>Tipo</Label>
                  <Select value={form.asset_type} onValueChange={(v) => setForm({ ...form, asset_type: v })}>
                    <SelectTrigger data-testid="asset-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger data-testid="asset-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ativo">Ativo</SelectItem>
                      <SelectItem value="manutencao">Em manutenção</SelectItem>
                      <SelectItem value="estoque">Em estoque</SelectItem>
                      <SelectItem value="inativo">Inativo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Marca</Label>
                  <Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} data-testid="asset-brand-input" /></div>
                <div><Label>Modelo</Label>
                  <Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} data-testid="asset-model-input" /></div>
                <div><Label>Número de série</Label>
                  <Input value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} data-testid="asset-serial-input" /></div>
                <div><Label>Patrimônio / Tag</Label>
                  <Input value={form.tag} onChange={(e) => setForm({ ...form, tag: e.target.value })} data-testid="asset-tag-input" /></div>
                <div className="col-span-2"><Label>Funcionário responsável</Label>
                  <Select value={form.assigned_to || "__none"} onValueChange={(v) => setForm({ ...form, assigned_to: v === "__none" ? "" : v })}>
                    <SelectTrigger data-testid="asset-assignee-select">
                      <SelectValue placeholder="Selecione um funcionário" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">— Sem responsável —</SelectItem>
                      {users.map((u) => (
                        <SelectItem key={u.id} value={u.id}>{u.name} ({u.email})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Departamento</Label>
                  <Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })}
                    placeholder="Ex.: RH, TI, Comercial" data-testid="asset-department-input" /></div>
                <div><Label>Localização</Label>
                  <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
                    placeholder="Ex.: Sala 12" data-testid="asset-location-input" /></div>
                <div className="col-span-2"><Label>Data de aquisição</Label>
                  <Input type="date" value={form.acquisition_date}
                    onChange={(e) => setForm({ ...form, acquisition_date: e.target.value })}
                    data-testid="asset-date-input" /></div>
                <div className="col-span-2"><Label>Observações</Label>
                  <Textarea rows={3} value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="asset-notes-input" /></div>
              </div>
              <DialogFooter><Button type="submit" data-testid="asset-save-btn">Salvar</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative min-w-64 flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder="Buscar por nome, série, patrimônio ou funcionário..."
            value={search} onChange={(e) => setSearch(e.target.value)} data-testid="asset-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-52" data-testid="asset-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os status</SelectItem>
            <SelectItem value="ativo">Ativo</SelectItem>
            <SelectItem value="manutencao">Manutenção</SelectItem>
            <SelectItem value="estoque">Em estoque</SelectItem>
            <SelectItem value="inativo">Inativo</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-hidden rounded-md border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Ativo</TableHead>
              <TableHead className="hidden md:table-cell">Tipo</TableHead>
              <TableHead className="hidden lg:table-cell">Série / Tag</TableHead>
              <TableHead>Funcionário</TableHead>
              <TableHead className="hidden md:table-cell">Departamento</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="py-10 text-center text-muted-foreground">Nenhum ativo cadastrado.</TableCell></TableRow>
            ) : filtered.map((a) => (
              <TableRow key={a.id} data-testid={`asset-row-${a.id}`}>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    <Laptop className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <div>{a.name}</div>
                      {(a.brand || a.model) && (
                        <div className="text-xs text-muted-foreground">{[a.brand, a.model].filter(Boolean).join(" · ")}</div>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell className="hidden md:table-cell text-muted-foreground">{a.asset_type}</TableCell>
                <TableCell className="hidden lg:table-cell font-mono text-xs text-muted-foreground">
                  {a.serial_number || "—"}{a.tag ? ` / ${a.tag}` : ""}
                </TableCell>
                <TableCell>
                  {a.assigned_to_name ? (
                    <span>{a.assigned_to_name}</span>
                  ) : (
                    <span className="text-xs text-muted-foreground italic">Sem responsável</span>
                  )}
                </TableCell>
                <TableCell className="hidden md:table-cell text-muted-foreground">{a.department || "—"}</TableCell>
                <TableCell><Badge className={STATUS[a.status]?.cls}>{STATUS[a.status]?.label || a.status}</Badge></TableCell>
                <TableCell className="text-right">
                  <Button size="icon" variant="ghost" onClick={() => openHistory(a)} data-testid={`history-asset-${a.id}`}>
                    <History className="h-4 w-4" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => openEdit(a)} data-testid={`edit-asset-${a.id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  {user.role === "admin" && (
                    <Button size="icon" variant="ghost" onClick={() => remove(a)} data-testid={`delete-asset-${a.id}`}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Histórico de movimentação</DialogTitle>
            <DialogDescription>{historyAsset?.name}</DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] space-y-3 overflow-y-auto">
            {history.length === 0 && (
              <p className="text-center text-sm text-muted-foreground py-8">Nenhuma movimentação registrada.</p>
            )}
            {history.map((h) => (
              <div key={h.id} className="rounded-md border border-border p-3" data-testid={`history-entry-${h.id}`}>
                <div className="flex items-center justify-between text-xs">
                  <Badge variant="outline">{h.event}</Badge>
                  <span className="font-mono text-muted-foreground">
                    {new Date(h.changed_at).toLocaleString("pt-BR")}
                  </span>
                </div>
                <div className="mt-2 text-sm">
                  Por <b>{h.changed_by_name}</b>
                </div>
                {(h.changes || []).length > 0 && (
                  <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                    {h.changes.map((c, i) => (
                      <li key={i}>
                        <b className="text-foreground">{c.field}:</b> {c.from ?? "—"} → {c.to ?? "—"}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
